import { hasYesOrNo, checkValue } from 'models/validate';
import nameModelValidator from 'forms/shared/validators/models/name/name';
import { passportPattern } from 'constants/patterns';
import { createDateFromObject, getSafeDateTime } from 'helpers/date';
import { DEFAULT_LATEST } from 'constants/dateLimits';
import { ValidateJSModel } from 'forms/shared/types';
import { DateBaseValidatorOptions } from 'forms/shared/validators/base/date/types';
import pvqPassportModelValidator from 'forms/shared/validators/models/pvqPassport';
import { isPVQForm } from 'helpers/form';
import { CitizenshipUSPassportModel } from './types';

const citizenshipUSPassportValidator: ValidateJSModel<CitizenshipUSPassportModel> = {
  HasPassports: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) { return {}; }

    if (options?.requiredDocumentsEmptySF852013) {
      return {
        presence: true,
        hasValue: {
          validator: {
            inclusion: {
              within: ['Yes'],
              message: 'citizenship.errors.missingRequiredDocumentsSF852013'
            }
          }
        }
      };
    }
    return {
      presence: true,
      hasValue: { validator: hasYesOrNo },
    };
  },

  Card: {},
  Comments: {},
  OptionalComments: {},
  requireCitizenshipUsPassportName: {},
  requireCitizenshipUsPassportExpiration: {},

  Name: (value, attributes, attributeName, options) => ((checkValue(attributes?.HasPassports, 'No')
    || isPVQForm(options?.formType))
    ? {}
    : {
      presence: true,
      model: { validator: nameModelValidator },
    }),

  Number: (value, attributes, attributeName, options) => {
    const { HasPassports, Issued } = attributes;
    if (checkValue(HasPassports, 'No') || isPVQForm(options?.formType)) { return {}; }

    // If issued in 1990 or after, must be 9 characters long
    const issuedDate = getSafeDateTime(Issued);

    const dateThreshold = createDateFromObject({ day: 1, month: 1, year: 1990 });
    if (issuedDate.valid && issuedDate.datetime && issuedDate.datetime >= dateThreshold) {
      // @ts-ignore bad typing with length. need to define type for validations
      return {
        presence: true,
        hasValue: {
          validator: {
            length: { is: 9, message: 'error.passport.length' },
            format: { pattern: passportPattern, message: 'error.passport.pattern' },
          },
        },
      };
    }

    return {
      presence: true,
      hasValue: true,
    };
  },

  Issued: (value, attributes, attributeName, options) => {
    if (checkValue(attributes?.HasPassports, 'No') || isPVQForm(options?.formType)) { return {}; }

    const { applicantBirthdate } = options;

    return {
      presence: true,
      date: { earliest: applicantBirthdate, latest: DEFAULT_LATEST },
    };
  },

  Expiration: (value, attributes, attributeName, options) => {
    if (checkValue(attributes?.HasPassports, 'No')
      || isPVQForm(options?.formType)) { return {}; }

    const { Issued } = attributes;
    const dateLimits: DateBaseValidatorOptions = {};

    if (Issued) {
      dateLimits.earliest = Issued;
      dateLimits.tooEarly = 'error.passport.expiration.min';
      const latestLimit = getSafeDateTime(Issued);

      if (latestLimit.valid) {
        latestLimit.datetime = latestLimit.datetime.plus({ years: 10 });
        if (attributes?.Issued?.month === '2' && attributes?.Issued?.day === '29') {
          latestLimit.datetime = latestLimit.datetime.endOf('month');
        }

        dateLimits.latest = latestLimit.datetime;
        dateLimits.tooLate = 'error.passport.expiration.max';
        dateLimits.requireYear = false; // ignore additional year validation
      }
    }

    return { presence: true, date: dateLimits };
  },
  HasPassportBook: (value, attributes, attributeName, options) => (isPVQForm(options?.formType) ? {
    presence: true,
    hasValue: { validator: hasYesOrNo },
  } : {}),
  HasPassportBookExplanation: (value, attributes, attributeName, options) => ((isPVQForm(options?.formType)) && attributes?.HasPassportBook?.value === 'IDontKnow' ? { presence: true, hasValue: true } : {}),
  PassportBooks: (value, attributes, attributeName, options) => ((isPVQForm(options?.formType) && attributes?.HasPassportBook?.value === 'Yes') ? {
    presence: true,
    accordion: { validator: pvqPassportModelValidator },
  } : {}),
  HasPassportCard: (value, attributes, attributeName, options) => (isPVQForm(options?.formType) ? {
    presence: true,
    hasValue: { validator: hasYesOrNo },
  } : {}),
  HasPassportCardExplanation: (value, attributes, attributeName, options) => ((isPVQForm(options?.formType)) && attributes?.HasPassportCard?.value === 'IDontKnow' ? { presence: true, hasValue: true } : {}),
  PassportCard: (value, attributes, attributeName, options) => ((isPVQForm(options?.formType) && attributes?.HasPassportCard?.value === 'Yes') ? {
    presence: true,
    model: { validator: pvqPassportModelValidator },
  } : {}),
};

export default citizenshipUSPassportValidator;
